/**
 * Studio Passione — comportamento dei blocchi "SP ·" della pagina prodotto.
 *
 * - Aggiorna prezzo, disponibilità e pulsante "Acquista ora" quando cambia la variante
 *   (ascolta lo standard event productSelect emesso dal variant picker del tema).
 * - Sposta il link "Guida alle taglie" accanto ai pulsanti della taglia (anche con Swatch King).
 * - Apre/chiude le finestre (dialog) dei blocchi.
 * - "Acquista ora": aggiunge la variante scelta al carrello e porta subito al checkout.
 */
import { StandardEvents } from '@shopify/events';

const LIVE_SELECTOR = '[data-sp-live]';
const SIZE_PATTERN = /tagli|taill|size|misur|talla|numer|gr(ö|o)(ß|ss)e/i;
const boundSections = new WeakSet();
const observedPickers = new WeakSet();

/** @param {Element} element */
const sectionOf = (element) => element.closest('.shopify-section');

/**
 * Copia il contenuto aggiornato dei nodi [data-sp-live] dall'HTML della sezione renderizzata.
 * @param {Element} section
 * @param {Document} html
 */
function updateLiveNodes(section, html) {
  for (const node of section.querySelectorAll(LIVE_SELECTOR)) {
    if (!(node instanceof HTMLElement)) continue;
    const key = node.dataset.spLive;
    if (!key) continue;
    const fresh = html.querySelector(`[data-sp-live="${CSS.escape(key)}"]`);
    if (fresh && fresh.innerHTML !== node.innerHTML) node.innerHTML = fresh.innerHTML;
  }
}

/**
 * Rimuove i due punti finali dalle etichette di Swatch King ("Taglia:" → "Taglia"),
 * così l'intestazione è uguale al design.
 * @param {Element} picker
 */
function normalizeAppLabels(picker) {
  for (const label of picker.querySelectorAll('.sk-group-label')) {
    const first = label.firstChild;
    if (first && first.nodeType === Node.TEXT_NODE && /:\s*$/.test(first.data)) {
      first.data = first.data.replace(/\s*:\s*$/, '');
    }
  }
}

/**
 * Trova il gruppo "Taglia" visibile: prima quello di Swatch King (se l'app è attiva),
 * poi il fieldset nativo del tema.
 * @param {Element} picker
 * @returns {{ host: Element, appGuide: Element | null } | null}
 */
function findSizeGroup(picker) {
  for (const group of picker.querySelectorAll('.sk-group')) {
    const label = group.querySelector('.sk-group-label');
    if (SIZE_PATTERN.test(label?.textContent ?? '')) {
      return { host: group.querySelector('.sk-swatches') ?? group, appGuide: group.querySelector('.sk-sc-btn') };
    }
  }
  const groups = picker.querySelectorAll('fieldset.variant-option, .variant-option--dropdowns');
  for (const group of groups) {
    if (group.classList.contains('sk-hidden') || getComputedStyle(group).display === 'none') continue;
    const label = group.querySelector('legend, label');
    if (SIZE_PATTERN.test(label?.textContent ?? '')) return { host: group, appGuide: null };
  }
  return null;
}

/**
 * Se l'app Kaching Bundles mostra le offerte, la quantità la gestisce l'app:
 * la sezione riceve la classe sp-has-bundle e il CSS nasconde "Quantità" e il selettore.
 * @param {Element} section
 */
function updateBundleState(section) {
  const bundle = section.querySelector('kaching-bundle');
  const visible = bundle instanceof HTMLElement && bundle.getBoundingClientRect().height > 0;
  section.classList.toggle('sp-has-bundle', visible);
}

/**
 * Inserisce il link della guida taglie accanto ai pulsanti della taglia.
 * Se Swatch King ha già una sua tabella taglie per il prodotto, usa quella e non duplica.
 * Senza JavaScript il link resta visibile nella sua posizione originale.
 * @param {Element} section
 */
function enhanceSection(section) {
  updateBundleState(section);
  const picker = section.querySelector('variant-picker');
  if (picker) normalizeAppLabels(picker);

  for (const guide of section.querySelectorAll('[data-sp-size-guide]')) {
    if (!(guide instanceof HTMLElement) || !picker) continue;
    const trigger = guide.querySelector(':scope > .sp-size-guide__trigger');
    const sizeGroup = findSizeGroup(picker);
    if (!trigger || !sizeGroup) continue;

    const id = guide.dataset.spSizeGuide ?? '';
    // Con data-sp-guide-priority="theme" (tabella del tema attiva) il link del tema prende il posto
    // di quello di Swatch King: il pulsante dell'app viene solo nascosto, la sua tabella resta nell'app.
    const preferTheme = guide.dataset.spGuidePriority === 'theme';
    let host = sizeGroup.appGuide ? null : sizeGroup.host;
    if (sizeGroup.appGuide && preferTheme) {
      host = sizeGroup.appGuide.parentElement;
      sizeGroup.appGuide.classList.add('sp-app-guide--hidden');
      if (sizeGroup.appGuide instanceof HTMLElement) sizeGroup.appGuide.style.setProperty('display', 'none', 'important');
    }
    for (const stale of section.querySelectorAll(`[data-sp-guide-clone="${CSS.escape(id)}"]`)) {
      if (stale.parentElement !== host) stale.remove();
    }
    if (host && !host.querySelector(`:scope > [data-sp-guide-clone="${CSS.escape(id)}"]`)) {
      const clone = /** @type {HTMLElement} */ (trigger.cloneNode(true));
      clone.dataset.spGuideClone = id;
      clone.classList.add('sp-size-guide__trigger--inline');
      host.appendChild(clone);
    }
    guide.classList.add('sp-size-guide--relocated');
  }

  // Le app (Swatch King, Kaching) e il morph del tema cambiano il DOM dopo il caricamento: riapplica.
  const details = section.querySelector('.product-details') ?? section;
  if (!observedPickers.has(details)) {
    observedPickers.add(details);
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        enhanceSection(section);
      });
    }).observe(details, { childList: true, subtree: true });
  }
}

/** @param {Element} section */
function bindSection(section) {
  if (boundSections.has(section)) return;
  boundSections.add(section);

  section.addEventListener(StandardEvents.productSelect, (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest('product-card, quick-add-dialog, dialog')) return;
    const promise = /** @type {any} */ (event).promise;
    if (!promise || typeof promise.then !== 'function') return;

    promise
      .then((/** @type {any} */ result) => {
        const html = result?.detail?.html;
        if (html instanceof Document) updateLiveNodes(section, html);
        enhanceSection(section);
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[sp-pdp]', error);
      });
  });
}

/** @param {ParentNode} root */
function init(root = document) {
  const sections = new Set();
  for (const element of root.querySelectorAll('[data-sp-pdp]')) {
    const section = sectionOf(element);
    if (section) sections.add(section);
  }
  for (const section of sections) {
    bindSection(section);
    enhanceSection(section);
  }
}

/**
 * "Acquista ora": invia variante e quantità a /cart/add con return_to=/checkout.
 * È un invio nativo del modulo (non fetch), così le app del carrello non aprono il drawer
 * e la cliente arriva direttamente al checkout.
 * @param {HTMLButtonElement} button
 */
function handleBuyNow(button) {
  const section = sectionOf(button);
  const form = section?.querySelector('form[data-type="add-to-cart-form"]');
  const status = button.closest('.sp-buy-now')?.querySelector('[data-sp-buy-now-status]');
  if (!(form instanceof HTMLFormElement)) {
    if (status) status.textContent = button.dataset.errorText || '';
    return;
  }

  const addButton = form.querySelector('[name="add"]');
  if (addButton instanceof HTMLButtonElement && addButton.disabled) return;

  const root = window.Shopify?.routes?.root || '/';
  const formData = new FormData(form);
  if (!formData.get('quantity')) formData.set('quantity', '1');

  const post = document.createElement('form');
  post.method = 'post';
  post.action = `${root}cart/add`;
  post.hidden = true;
  for (const [name, value] of formData.entries()) {
    if (typeof value !== 'string' || name === 'return_to') continue;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    post.appendChild(input);
  }
  const returnTo = document.createElement('input');
  returnTo.type = 'hidden';
  returnTo.name = 'return_to';
  returnTo.value = `${root}checkout`;
  post.appendChild(returnTo);

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  document.body.appendChild(post);
  post.submit();
}

// Tornando indietro dal checkout (cache del browser) riattiva il pulsante.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  for (const button of document.querySelectorAll('[data-sp-buy-now][aria-busy]')) {
    if (!(button instanceof HTMLButtonElement)) continue;
    button.removeAttribute('aria-busy');
    button.disabled = false;
  }
});

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const opener = target.closest('[data-sp-open-dialog]');
  if (opener instanceof HTMLElement) {
    const dialog = document.getElementById(opener.dataset.spOpenDialog ?? '');
    if (dialog instanceof HTMLDialogElement) {
      event.preventDefault();
      dialog.showModal();
    }
    return;
  }

  const closer = target.closest('[data-sp-close-dialog]');
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }

  if (target instanceof HTMLDialogElement && target.classList.contains('sp-dialog')) {
    target.close();
    return;
  }

  const buyNow = target.closest('[data-sp-buy-now]');
  if (buyNow instanceof HTMLButtonElement) {
    event.preventDefault();
    handleBuyNow(buyNow);
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) init(event.target);
});
